import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Box,
    Button,
    Grid,
    TextField,
    Typography,
    Alert,
    Snackbar,
    Card,
    CardContent,
    InputAdornment,
    IconButton,
    Chip
} from '@mui/material';
import * as Yup from 'yup';
import { Formik } from 'formik';
import MainCard from 'ui-component/cards/MainCard';
import Loading from 'components/Loading/Loading';
import { uploadCronogramaAnual, getEmailsCopia, updateEmailsCopia } from 'services/cronogramaAnual';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import SaveIcon from '@mui/icons-material/Save';
import { gridSpacing } from 'store/constant';

const CronogramaAnual = ({ ...others }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [loadingEmailsSave, setLoadingEmailsSave] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [emailsCopia, setEmailsCopia] = useState([]);
    const [emailInput, setEmailInput] = useState('');
    const [loadingEmails, setLoadingEmails] = useState(false);
    const idUnit = useSelector((state) => state.user.unit || '');
    const currentYear = new Date().getFullYear();

    const themeButton = createTheme({
        status: {
            danger: '#e53e3e'
        },
        palette: {
            primary: {
                main: '#0971f1',
                darker: '#053e85'
            }
        }
    });

    const validationSchema = Yup.object().shape({
        ano: Yup.number()
            .required('Ano é obrigatório')
            .min(2000, 'Ano deve ser maior que 2000')
            .max(2100, 'Ano deve ser menor que 2100')
            .integer('Ano deve ser um número inteiro'),
        file: Yup.mixed()
            .required('Arquivo é obrigatório')
            .test('fileType', 'Apenas arquivos Excel são permitidos (.xlsx, .xls)', (value) => {
                if (!value) return false;
                const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
                const validExtensions = ['xlsx', 'xls'];
                const fileName = value.name || '';
                const fileExtension = fileName.split('.').pop()?.toLowerCase();
                return validTypes.includes(value.type) || validExtensions.includes(fileExtension);
            })
    });

    const handleFileChange = (event, setFieldValue) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setFieldValue('file', file);
        }
    };

    const handleRemoveFile = (setFieldValue) => {
        setSelectedFile(null);
        setFieldValue('file', null);
    };

    const handleAddEmail = () => {
        const email = emailInput.trim();
        if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            if (!emailsCopia.includes(email)) {
                setEmailsCopia([...emailsCopia, email]);
                setEmailInput('');
            } else {
                setError('Este email já foi adicionado.');
                setTimeout(() => setError(''), 3000);
            }
        } else {
            setError('Por favor, insira um email válido.');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleRemoveEmail = (emailToRemove) => {
        setEmailsCopia(emailsCopia.filter(email => email !== emailToRemove));
    };

    const loadEmailsCopia = async (ano) => {
        if (!idUnit || idUnit === '14725896312' || !ano) {
            return;
        }

        try {
            setLoadingEmails(true);
            const response = await getEmailsCopia(idUnit, ano);
            if (response.data.success) {
                setEmailsCopia(response.data.data || []);
            }
        } catch (err) {
            console.error('Erro ao carregar emails em cópia:', err);
            // Não mostrar erro para o usuário, apenas logar
        } finally {
            setLoadingEmails(false);
        }
    };

    // Carregar emails quando o componente montar ou quando o ano mudar
    useEffect(() => {
        loadEmailsCopia(currentYear);
    }, []);

    const handleSaveEmails = async (ano) => {
        try {
            if (!idUnit || idUnit === '14725896312') {
                setError('Selecione uma unidade válida para atualizar os emails.');
                setTimeout(() => setError(''), 4000);
                return;
            }
            if (!ano) {
                setError('Informe o ano para atualizar os emails.');
                setTimeout(() => setError(''), 4000);
                return;
            }

            setLoadingEmailsSave(true);
            setError('');
            setSuccess('');

            const response = await updateEmailsCopia(idUnit, ano, emailsCopia);
            if (response.data.success) {
                setSuccess(response.data.success);
                setEmailsCopia(response.data.data || emailsCopia);
                setTimeout(() => setSuccess(''), 5000);
            } else {
                setError(response.data.error || 'Erro ao atualizar emails.');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao atualizar emails. Tente novamente.');
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoadingEmailsSave(false);
        }
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');

            if (!selectedFile) {
                setError('Por favor, selecione um arquivo.');
                setLoading(false);
                return;
            }

            const response = await uploadCronogramaAnual(selectedFile, idUnit, values.ano, emailsCopia);

            if (response.data.success) {
                setSuccess(response.data.success);
                setSelectedFile(null);
                setEmailInput('');
                // Recarregar emails após salvar para mostrar os atualizados
                if (idUnit && idUnit !== '14725896312' && values.ano) {
                    loadEmailsCopia(values.ano);
                }
                resetForm();
                setTimeout(() => {
                    setSuccess('');
                }, 5000);
            } else {
                setError(response.data.error || 'Erro ao fazer upload do arquivo.');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.error || 'Erro ao fazer upload do arquivo. Tente novamente.';
            setError(errorMessage);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <ThemeProvider theme={themeButton}>
            <Box>
                <Formik
                    initialValues={{
                        ano: currentYear,
                        file: null
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ errors, touched, handleSubmit, setFieldValue, values }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={gridSpacing}>
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <strong>Instruções:</strong>
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                                                <li>A planilha deve ter as seguintes colunas: Equipamento (A), Quantidade (B), JAN (C), FEV (D), MAR (E), ABR (F), MAI (G), JUN (H), JUL (I), AGO (J), SET (K), OUT (L), NOV (M), DEZ (N)</li>
                                                <li>Nas colunas dos meses, informe os dias que terão manutenção (ex: "27" ou "21 e 22" ou "1, 2, 3")</li>
                                                <li>Apenas uma planilha por ano será permitida. Ao fazer upload de uma nova planilha para o mesmo ano, os dados anteriores serão substituídos</li>
                                            </ul>
                                        </Typography>
                                    </Alert>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Ano"
                                        name="ano"
                                        type="number"
                                        value={values.ano}
                                        onChange={(e) => {
                                            const novoAno = parseInt(e.target.value) || '';
                                            setFieldValue('ano', novoAno);
                                            // Carregar emails quando o ano mudar
                                            if (novoAno && idUnit && idUnit !== '14725896312') {
                                                loadEmailsCopia(novoAno);
                                            }
                                        }}
                                        error={touched.ano && Boolean(errors.ano)}
                                        helperText={touched.ano && errors.ano}
                                        InputProps={{
                                            inputProps: { min: 2000, max: 2100 }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <input
                                            accept=".xlsx,.xls"
                                            style={{ display: 'none' }}
                                            id="file-upload"
                                            type="file"
                                            onChange={(e) => handleFileChange(e, setFieldValue)}
                                        />
                                        <label htmlFor="file-upload">
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                fullWidth
                                                startIcon={<CloudUploadIcon />}
                                                disabled={loading}
                                            >
                                                Selecionar Arquivo Excel
                                            </Button>
                                        </label>
                                        {touched.file && errors.file && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                {errors.file}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>

                                {selectedFile && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <DescriptionIcon color="primary" />
                                                        <Box>
                                                            <Typography variant="body1" fontWeight="bold">
                                                                {selectedFile.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {(selectedFile.size / 1024).toFixed(2)} KB
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveFile(setFieldValue)}
                                                        disabled={loading}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Card variant="outlined" sx={{ backgroundColor: '#f5f5f5' }}>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                <EmailIcon color="primary" />
                                                <Typography variant="h6">
                                                    Emails em Cópia (CC)
                                                </Typography>
                                            </Box>
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                <Typography variant="body2">
                                                    O email do gerente da unidade já está incluído automaticamente.
                                                    Adicione aqui emails adicionais que devem receber cópia do cronograma semanal.
                                                    Você pode salvar apenas os emails, sem precisar reenviar a planilha.
                                                </Typography>
                                            </Alert>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={8}>
                                                    <TextField
                                                        fullWidth
                                                        label="Email"
                                                        type="email"
                                                        value={emailInput}
                                                        onChange={(e) => setEmailInput(e.target.value)}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                handleAddEmail();
                                                            }
                                                        }}
                                                        placeholder="exemplo@email.com"
                                                        disabled={loading || loadingEmailsSave}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        startIcon={<AddIcon />}
                                                        onClick={handleAddEmail}
                                                        disabled={loading || loadingEmailsSave || !emailInput.trim()}
                                                    >
                                                        Adicionar Email
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                            {emailsCopia.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                        Emails adicionados ({emailsCopia.length}):
                                                    </Typography>
                                                    <Box display="flex" flexWrap="wrap" gap={1}>
                                                        {emailsCopia.map((email, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={email}
                                                                onDelete={() => handleRemoveEmail(email)}
                                                                deleteIcon={<DeleteIcon />}
                                                                color="primary"
                                                                variant="outlined"
                                                                disabled={loading || loadingEmailsSave}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    color="secondary"
                                                    startIcon={loadingEmailsSave ? <Loading /> : <SaveIcon />}
                                                    onClick={() => handleSaveEmails(values.ano)}
                                                    disabled={loading || loadingEmailsSave || !values.ano}
                                                >
                                                    {loadingEmailsSave ? 'Salvando emails...' : 'Salvar apenas emails'}
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box display="flex" gap={2} justifyContent="flex-end">
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={loading || loadingEmailsSave || !selectedFile}
                                            startIcon={loading ? <Loading /> : <CloudUploadIcon />}
                                        >
                                            {loading ? 'Enviando...' : 'Enviar Cronograma'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>

                <Snackbar
                    open={!!success}
                    autoHideDuration={6000}
                    onClose={() => setSuccess('')}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={() => setError('')}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default CronogramaAnual;

